import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { forgetPassword, login, redeemMagicLink } from "./authActions";
import { instance } from "../../axios/axios";
import { REHYDRATE } from "redux-persist";
import { clearStoragePreservingPrefs, markJustLoggedIn } from "../../lib/utils";

const initialState = {
  loading: false,
  userInfo: null,
  error: null,
  token: null,
  isHydrated: false,
  detailsLoading: false,
  detailsError: null,
  detailsData: null,
  kindredPoints: null, // Add new field
  userInfoLastFetched: null, // Add new field
  // Impersonation (Quick Log feature, PR-C). When the viewer (Levi) starts an
  // impersonation session, the original token+userInfo are saved into viewer*
  // and the active token+userInfo become the impersonated employee's. Stop
  // restores them. The 401 swap-back interceptor (axios/axios.js) and the
  // session-expiry handler (App.js) both check isImpersonating to decide
  // between "swap back to viewer" and "log out completely".
  viewerToken: null,
  viewerInfo: null,
  isImpersonating: false,
  impersonationTarget: null,
  // No-login Friend tournament editor (thread 352, step 1). When the editor
  // route loads, it reads the k:"tedit" token from ?et= and stores it here so
  // tournamentApi's base query can attach it as X-Editor-Token on the creator
  // console's READ calls (step 2 activates that). Session-only: nulled on
  // REHYDRATE (never trusted across a reload) and on logout (initialState), so a
  // stale token can never ride a later request. Only consulted when there's no
  // rep bearer, mirroring the BFF's checkUserOrEditorToken precedence.
  editorToken: null,
};

const setLoading = (state) => {
  state.loading = true;
  state.error = null;
};

const setError = (state, action) => {
  state.error = action.payload || "An error occurred";
  state.loading = false;
};

export const silentRefetchUserInfo = createAsyncThunk(
  "auth/silentRefetchUserInfo",
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const token = state.auth?.token;

      const response = await instance.get("/user/portal/get-user-info", {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      return response.data;
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || "An error occurred.",
        status: error.response?.status ?? null,
        // e.g. "friend_portal_window_closed" — App.js shows that message on the
        // login screen instead of a bare "Session Expired".
        code: error.response?.data?.code ?? null,
      });
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: () => {
      // Clears storage but preserves per-device UI prefs (menu order/colors/
      // pinned landing page, button positions, sales-recording tags, payroll-
      // admin prefs, dismissed banners) — single source of truth in utils.js.
      clearStoragePreservingPrefs();

      return { ...initialState, isHydrated: true };
    },
    setKindredPoints: (state, action) => {
      const points =
        action.payload?.data?.[0]?.fields?.["Balance Points"];
      if (points !== undefined && points !== null) {
        state.kindredPoints = points;
      }
    },
    optimisticDeduct: (state, action) => {
      const amount = Number(action.payload) || 0;
      const base =
        typeof state.kindredPoints === "number"
          ? state.kindredPoints
          : state.userInfo?.["Balance Points"] ?? 0;
      state.kindredPoints = Math.max(0, base - amount);
    },
    revertOptimisticDeduct: (state, action) => {
      const amount = Number(action.payload) || 0;
      const base =
        typeof state.kindredPoints === "number"
          ? state.kindredPoints
          : state.userInfo?.["Balance Points"] ?? 0;
      state.kindredPoints = base + amount;
    },
    startImpersonation: (state, action) => {
      // Stash the viewer's original token/identity so /stop (or 401 swap-back)
      // can restore them without another login round-trip. Then swap the
      // active token to the impersonation JWT minted by POST /auth/impersonate/start.
      // Caller is responsible for dispatching silentRefetchUserInfo afterwards
      // to populate userInfo with the impersonated employee's record.
      const { token, target } = action.payload || {};
      if (!token) return;
      if (!state.isImpersonating) {
        state.viewerToken = state.token;
        state.viewerInfo = state.userInfo;
      }
      state.token = token;
      state.userInfo = null;
      state.isImpersonating = true;
      state.impersonationTarget = target || null;
      state.kindredPoints = null;
    },
    // No-login Friend tournament editor (thread 352, step 1). The editor route
    // dispatches setEditorToken(et) on mount (step 2) so the base query can send
    // X-Editor-Token on the console's reads; clearEditorToken drops it on unmount.
    setEditorToken: (state, action) => {
      state.editorToken = action.payload || null;
    },
    clearEditorToken: (state) => {
      state.editorToken = null;
    },
    stopImpersonation: (state) => {
      // Restore the saved viewer token/identity. Only dispatched from the
      // explicit Swap Back button in ImpersonationBanner (after a successful
      // POST /auth/impersonate/stop). The 401 auto-swap-back path was removed
      // 2026-05-27 per Levi.
      if (!state.viewerToken) return;
      state.token = state.viewerToken;
      state.userInfo = state.viewerInfo;
      state.viewerToken = null;
      state.viewerInfo = null;
      state.isImpersonating = false;
      state.impersonationTarget = null;
      state.kindredPoints = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(REHYDRATE, (state, action) => {
        // When rehydrating from persisted state, ensure loading is false
        if (!action.payload?.auth) {
          // Nothing was persisted: a first visit on this device, or iOS Safari
          // evicted localStorage. There is no session to restore, but the app
          // MUST still leave the hydrating state — ProtectedRoute renders null
          // until isHydrated is true, and nothing else ever sets it on this
          // path (login.fulfilled can't run if the login screen never renders).
          // Returning early here left every protected URL — an Event Chat
          // notification's "Open", a /p/<section> deep link, /home — a
          // permanently blank white page instead of the login screen.
          // Thread #1727, msg 1a16084ba663eb0c.
          state.isHydrated = true;
          return;
        }
        const persisted = action.payload.auth;
        // Quick Log: never trust persisted impersonation across a session
        // boundary. Without auto-swap-back, a stale T_imp from a closed tab
        // would silently keep the viewer in someone else's portal on next
        // load (the only exit is the Swap Back button, which can't render
        // its "you're acting as X" banner correctly off a stale target).
        // Always rehydrate as the viewer; the user re-runs Quick Log if
        // they still want to act as someone.
        const cleanAuth = (persisted.isImpersonating || persisted.viewerToken)
          ? {
              ...persisted,
              token: persisted.viewerToken || persisted.token,
              userInfo: persisted.viewerInfo || persisted.userInfo,
              viewerToken: null,
              viewerInfo: null,
              isImpersonating: false,
              impersonationTarget: null,
            }
          : persisted;
        return {
          ...cleanAuth,
          loading: false,
          isHydrated: true,
          // Session-only (thread 352): never rehydrate a persisted editor token —
          // it's read fresh from ?et= on each editor-route visit.
          editorToken: null,
        };
      })
      .addCase(login.pending, setLoading)
      .addCase(login.fulfilled, (state, { payload }) => {
        state.token = payload.data.token;
        state.userInfo = payload?.data;
        state.loading = false;
        // Without this, a first-ever login (no persisted state in localStorage)
        // never gets isHydrated=true — REHYDRATE already fired with no stored
        // auth, and there is no subsequent action that sets it. Home.jsx's
        // skipRoleDetails guard stays true forever and the skeleton never resolves.
        state.isHydrated = true;
        // Tell Home this is a fresh login → land on the pinned page. A refresh
        // never sets this, so refresh stays on the current tab/scroll.
        markJustLoggedIn();
      })
      .addCase(login.rejected, (state, { payload }) => {
        state.error = payload || "An error occurred";
        state.loading = false;
        state.userInfo = null;
        state.token = null;
      })
      // Magic-link redeem returns { data: { jwt, user: { id, name, role } } }
      // — shape differs from /login (which returns the full Airtable employee
      // record as data). We hydrate the JWT here and let PortalLink follow up
      // with silentRefetchUserInfo to populate userInfo with the full record.
      .addCase(redeemMagicLink.pending, setLoading)
      .addCase(redeemMagicLink.fulfilled, (state, { payload }) => {
        state.token = payload?.data?.jwt || null;
        state.userInfo = payload?.data?.user || null;
        state.loading = false;
        // Without isHydrated=true a first-ever magic-link login (no persisted
        // auth in localStorage) never leaves Home.jsx's skeleton — the same
        // failure login.fulfilled below documents, and the identical guard
        // (ProtectedRoute returns null until isHydrated) keeps the whole tree
        // blank. A brand-new Friend taps the welcome link and would see an
        // eternal loading shell. Magic-link redeem is also a fresh login →
        // land on the pinned page.
        state.isHydrated = true;
        markJustLoggedIn();
      })
      .addCase(redeemMagicLink.rejected, (state, { payload }) => {
        state.error = payload || "An error occurred";
        state.loading = false;
        state.userInfo = null;
        state.token = null;
      })
      .addCase(forgetPassword.pending, setLoading)
      .addCase(forgetPassword.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(forgetPassword.rejected, (state, action) => {
        setError(state, action);
      })
      .addCase(silentRefetchUserInfo.fulfilled, (state, { payload }) => {
        state.userInfo = payload?.data;
        state.userInfoLastFetched = Date.now();
      })
      .addCase(silentRefetchUserInfo.rejected, (state, action) => {
        state.error = action.payload?.message || action.payload || "Failed to refresh user info";
      });
  },
});

export const {
  logout,
  setKindredPoints,
  optimisticDeduct,
  revertOptimisticDeduct,
  startImpersonation,
  stopImpersonation,
  setEditorToken,
  clearEditorToken,
} = authSlice.actions;
export default authSlice.reducer;

/**
 * import { createSlice } from "@reduxjs/toolkit";
import { forgetPassword, login } from "./authActions";

const initialState = {
  loading: false,
  userInfo: null,
  error: null,
  token: null,
  detailsLoading: false,
  detailsError: null,
  detailsData: null,
};

// Function to check token expiration
const isTokenExpired = () => {
  const tokenExpiry = localStorage.getItem("tokenExpiry");
  if (!tokenExpiry) return true;
  return Date.now() > parseInt(tokenExpiry, 10);
};

// Save token and expiration to localStorage
const saveTokenWithExpiry = (token) => {
  const expiryTime = Date.now() + 60 * 60 * 1000; // 1 hour in milliseconds
  localStorage.setItem("token", token);
  localStorage.setItem("tokenExpiry", expiryTime.toString());
};

// Clear token and expiration from localStorage
const clearToken = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("tokenExpiry");
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: (state) => {
      clearToken();
      return { ...initialState };
    },
    checkTokenExpiration: (state) => {
      if (isTokenExpired()) {
        clearToken();
        return { ...initialState };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, { payload }) => {
        state.token = payload.data.token;
        state.userInfo = payload?.data;
        state.loading = false;
        saveTokenWithExpiry(payload.data.token);
      })
      .addCase(login.rejected, (state, { payload }) => {
        state.error = payload || "An error occurred";
        state.loading = false;
        state.userInfo = null;
        state.token = null;
        clearToken();
      })
      .addCase(forgetPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(forgetPassword.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(forgetPassword.rejected, (state, action) => {
        state.error = action.payload || "An error occurred";
        state.loading = false;
      });
  },
});

export const { logout, checkTokenExpiration } = authSlice.actions;
export default authSlice.reducer;



store.dispatch(checkTokenExpiration());

 */
